import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { DeviceError } from '../../constants/DeviceError';
import { STREAM_CONFIG } from '../../constants/streamConfig';
import { getDesktopStream, getWebcamStream } from '../../core/mediaDevices';
export const StreamDeviceTypes = {
  Webcam: 'webcam',
  ScreenSharing: 'screensharing'
};
export default class StreamManager {
  constructor(streamConfigOverrides = {}) {
    this.screenStream = void 0;
    this.webcamStream = void 0;
    this.streamConfigOverrides = streamConfigOverrides;
    this.errors = {};
  }
  async getStreams(config = {}) {
    this._cleanErrors();
    if (config[StreamDeviceTypes.ScreenSharing]) {
      var _config$StreamDeviceT, _this$streamConfigOve, _this$streamConfigOve2, _config$StreamDeviceT2, _this$streamConfigOve3, _this$streamConfigOve4;
      this.screenStream = await getDesktopStream(_objectSpread({
        video: _objectSpread(_objectSpread(_objectSpread({}, STREAM_CONFIG['desktop'].video), (_config$StreamDeviceT = config[StreamDeviceTypes.ScreenSharing].video) !== null && _config$StreamDeviceT !== void 0 ? _config$StreamDeviceT : {}), (_this$streamConfigOve = (_this$streamConfigOve2 = this.streamConfigOverrides['desktop']) === null || _this$streamConfigOve2 === void 0 ? void 0 : _this$streamConfigOve2.video) !== null && _this$streamConfigOve !== void 0 ? _this$streamConfigOve : {}),
        audio: _objectSpread(_objectSpread(_objectSpread({}, STREAM_CONFIG['desktop'].audio), (_config$StreamDeviceT2 = config[StreamDeviceTypes.ScreenSharing].audio) !== null && _config$StreamDeviceT2 !== void 0 ? _config$StreamDeviceT2 : {}), (_this$streamConfigOve3 = (_this$streamConfigOve4 = this.streamConfigOverrides['desktop']) === null || _this$streamConfigOve4 === void 0 ? void 0 : _this$streamConfigOve4.audio) !== null && _this$streamConfigOve3 !== void 0 ? _this$streamConfigOve3 : {})
      }, config[StreamDeviceTypes.ScreenSharing].experimentalOptions)).catch(e => {
        this.errors[DeviceError.ScreenSharing] = e;
      });
    }
    if (config[StreamDeviceTypes.Webcam] && this.screenStream) {
      var _config$StreamDeviceT3, _config$StreamDeviceT4, _this$streamConfigOve5, _this$streamConfigOve6, _config$StreamDeviceT5, _this$streamConfigOve7, _this$streamConfigOve8;
      this.webcamStream = await this._getWebcamStream({
        audioDeviceId: (_config$StreamDeviceT3 = config[StreamDeviceTypes.Webcam].audio) === null || _config$StreamDeviceT3 === void 0 ? void 0 : _config$StreamDeviceT3.deviceId,
        audioConfig: _objectSpread(_objectSpread(_objectSpread({}, STREAM_CONFIG['desktopWebcam'].audio), (_config$StreamDeviceT4 = config[StreamDeviceTypes.Webcam].audio) !== null && _config$StreamDeviceT4 !== void 0 ? _config$StreamDeviceT4 : {}), (_this$streamConfigOve5 = (_this$streamConfigOve6 = this.streamConfigOverrides['desktopWebcam']) === null || _this$streamConfigOve6 === void 0 ? void 0 : _this$streamConfigOve6.audio) !== null && _this$streamConfigOve5 !== void 0 ? _this$streamConfigOve5 : {}),
        videoDeviceId: (_config$StreamDeviceT5 = config[StreamDeviceTypes.Webcam].video) === null || _config$StreamDeviceT5 === void 0 ? void 0 : _config$StreamDeviceT5.deviceId,
        videoConfig: _objectSpread(_objectSpread(_objectSpread({}, STREAM_CONFIG['desktopWebcam'].video), config[StreamDeviceTypes.Webcam].video), (_this$streamConfigOve7 = (_this$streamConfigOve8 = this.streamConfigOverrides['desktopWebcam']) === null || _this$streamConfigOve8 === void 0 ? void 0 : _this$streamConfigOve8.video) !== null && _this$streamConfigOve7 !== void 0 ? _this$streamConfigOve7 : {})
      });
    }
    if (config[StreamDeviceTypes.Webcam] && !this.screenStream) {
      var _config$StreamDeviceT6, _config$StreamDeviceT7, _this$streamConfigOve9, _this$streamConfigOve10, _config$StreamDeviceT8, _config$StreamDeviceT9, _this$streamConfigOve11, _this$streamConfigOve12;
      this.webcamStream = await this._getWebcamStream({
        audioDeviceId: (_config$StreamDeviceT6 = config[StreamDeviceTypes.Webcam].audio) === null || _config$StreamDeviceT6 === void 0 ? void 0 : _config$StreamDeviceT6.deviceId,
        audioConfig: _objectSpread(_objectSpread(_objectSpread({}, STREAM_CONFIG['standaloneWebcam'].audio), (_config$StreamDeviceT7 = config[StreamDeviceTypes.Webcam].audio) !== null && _config$StreamDeviceT7 !== void 0 ? _config$StreamDeviceT7 : {}), (_this$streamConfigOve9 = (_this$streamConfigOve10 = this.streamConfigOverrides['standaloneWebcam']) === null || _this$streamConfigOve10 === void 0 ? void 0 : _this$streamConfigOve10.audio) !== null && _this$streamConfigOve9 !== void 0 ? _this$streamConfigOve9 : {}),
        videoDeviceId: (_config$StreamDeviceT8 = config[StreamDeviceTypes.Webcam].video) === null || _config$StreamDeviceT8 === void 0 ? void 0 : _config$StreamDeviceT8.deviceId,
        videoConfig: _objectSpread(_objectSpread(_objectSpread({}, STREAM_CONFIG['standaloneWebcam'].video), (_config$StreamDeviceT9 = config[StreamDeviceTypes.Webcam].video) !== null && _config$StreamDeviceT9 !== void 0 ? _config$StreamDeviceT9 : {}), (_this$streamConfigOve11 = (_this$streamConfigOve12 = this.streamConfigOverrides['standaloneWebcam']) === null || _this$streamConfigOve12 === void 0 ? void 0 : _this$streamConfigOve12.video) !== null && _this$streamConfigOve11 !== void 0 ? _this$streamConfigOve11 : {})
      });
    }
    return this._getCachedStreams();
  }
  async removeWebcamStream() {
    if (this.webcamStream) {
      this.webcamStream.getTracks().forEach(track => track.stop());
      this.webcamStream = void 0;
    }
  }
  async removeScreenStream() {
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(track => track.stop());
      this.screenStream = void 0;
    }
  }
  async removeStreams(types = []) {
    if (types.includes(StreamDeviceTypes.Webcam)) {
      await this.removeWebcamStream();
    }
    if (types.includes(StreamDeviceTypes.ScreenSharing)) {
      await this.removeScreenStream();
    }
    return this._getCachedStreams();
  }
  async removeAllStreams() {
    await this.removeWebcamStream();
    await this.removeScreenStream();
  }
  _cleanErrors() {
    this.errors = {};
  }
  async _getWebcamStream({
    videoConfig,
    audioConfig,
    audioDeviceId,
    videoDeviceId
  }) {
    try {
      // user can disallow usage for any device separately so we need to test availability of each
      return await getWebcamStream({
        video: videoConfig,
        audio: audioConfig,
        audioDeviceId,
        videoDeviceId
      });
    } catch (e) {
      // assume that previous error comes from mic
      // if next fallback is successful we confirm our assumption
      this.errors[DeviceError.Mic] = e;
    }
    try {
      // try to get video stream
      return await getWebcamStream({
        video: videoConfig,
        videoDeviceId
      });
    } catch (e) {
      this.errors[DeviceError.Webcam] = e;
      // we proved our mic assumption to be false
      // so clear mic error for a time being.
      // If mic error persists it will be caught further on
      this.errors[DeviceError.Mic] = void 0;
    }
    try {
      // try to get audio stream
      return await getWebcamStream({
        audio: audioConfig,
        audioDeviceId
      });
    } catch (e) {
      this.errors[DeviceError.Mic] = e;
    }
  }
  _getCachedStreams() {
    return {
      [StreamDeviceTypes.Webcam]: this.webcamStream,
      [StreamDeviceTypes.ScreenSharing]: this.screenStream,
      errors: this.errors
    };
  }
}